import { PreferenceTier } from './preferences.types';

/**
 * Which tiers a user may save the grid view to, and what resetting one falls back
 * to.
 *
 * Pure, because these are authorization-shaped rules rendered as buttons, and a
 * button that appears when it should not is indistinguishable from one that works
 * until the server refuses it. The SERVER is the control — it resolves the caller
 * from the token and enforces every tier (see PreferencesAuthz). This decides what
 * to OFFER, and the two must agree or users get 403s from buttons the app drew for
 * them.
 */

export interface GridSaveContext {
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  /** The company has locked the grid view: the user tier is ignored for it. */
  companyLocked: boolean;
}

export interface TierOption {
  tier: PreferenceTier;
  label: string;
  /** Why this tier is unavailable, or null when it is. */
  disabledReason: string | null;
}

/**
 * The save targets to render, most specific first — which is the order the cascade
 * resolves in reverse, and the order a user thinks in: me, then my company, then
 * everyone.
 *
 * A tier the user cannot write is OMITTED, not disabled. That differs from the tab
 * gating on purpose: a greyed tab teaches a user that a feature exists and how to
 * reach it, whereas a greyed "Save as global default" teaches a technician only
 * that there is a power they will never have.
 *
 * The exception is a user tier the COMPANY has locked. That one is shown disabled
 * with the reason, because the user could save yesterday and the explanation is the
 * whole point — silently dropping the button would read as a bug.
 */
export function saveTargets(context: GridSaveContext, companyName?: string | null): TierOption[] {
  const options: TierOption[] = [
    {
      tier: 'user',
      label: 'Save for me',
      disabledReason: context.companyLocked
        ? 'Your company administrator has locked the project grid layout'
        : null,
    },
  ];

  if (context.isCompanyAdmin || context.isSuperAdmin) {
    options.push({
      tier: 'company',
      label: companyName ? `Save for ${companyName}` : 'Save for my company',
      disabledReason: null,
    });
  }

  if (context.isSuperAdmin) {
    options.push({
      tier: 'global',
      label: 'Save as global default',
      disabledReason: null,
    });
  }

  return options;
}

/**
 * What clearing this tier falls back to, named for the button.
 *
 * The label names the DESTINATION rather than the action, because "Reset" alone
 * does not tell a user whose layout they are about to get — and for an admin
 * clearing the company tier, that is the difference between their own default and
 * the product's.
 */
export function resetLabel(tier: PreferenceTier, context: GridSaveContext, companyName?: string | null): string {
  switch (tier) {
    case 'user':
      // A user's reset falls to the company tier when there is a company to fall to.
      return context.isCompanyAdmin || context.isSuperAdmin || companyName
        ? `Reset to ${companyName ?? 'company'} default`
        : 'Reset to default';
    case 'company':
      return 'Reset to global default';
    case 'global':
      return 'Reset to built-in default';
  }
}

/** The tier a plain "Save" targets: always the caller's own. Everyone owns their own view. */
export const DEFAULT_SAVE_TIER: PreferenceTier = 'user';

/**
 * May this user reset this tier? Same permission as writing it — a reset IS a write
 * (an explicit null), so anything else would let someone clear a layout they could
 * not set.
 */
export function canResetTier(tier: PreferenceTier, context: GridSaveContext): boolean {
  const option = saveTargets(context).find((o) => o.tier === tier);
  return !!option && option.disabledReason === null;
}
